import { useEffect, memo, useCallback } from 'react'
import { observer } from 'mobx-react'

import { useSaveButton } from 'hooks/useSaveButton'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { useStores } from 'stores/utils/hooks/useStores'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import {
  CONFIG_DDF,
  CONFIG_DDF_RCE_EDIT,
  CONFIG_SECOND_DEBTOR,
  isFFGConfigWithAutoRedirectOnDacSuccess
} from 'config/utils/config'
import { NavigateFunction } from 'react-router-dom'
import LegalHintTexts from 'texts/common/copy/legal'
import {
  RegisteredEventName,
  broadcastToParent
} from '@finanzcheck/catalyst-pollard'
import { COOKIE_KEY_FCID, getCookie } from 'utils/cookie'
import { getOptimizelyTrackingAttribute } from 'utils/tracking'
import { usePayoutAccountValidation } from 'hooks/usePayoutAccountValidation'
import { getButtonSizes } from './getButtonSizes'
import FormNavigation from './FormNavigationTemplate'
import { getAge } from 'utils/date'
import { AgeGroup } from 'constants/components'
import { useAgeGroup } from 'stores/utils/hooks/useAgeGroup'
import { Button } from '@mui/material'
import { Experiments } from 'CustomOptimizelyProvider'
import { PossibleVariants } from 'utils/tracking/optimizely/types'
import { isYoungOrMiddleAgeGroup } from 'stores/helpers/AgeGroups'

type Props = {
  onBack?: () => void
  onNext: () => void
  legalContact: string
  navigate: NavigateFunction
}

const FFGFormNavigation = observer(({ onBack, onNext, navigate }: Props) => {
  const {
    rootStore: {
      isInIframe,
      urlParams: { backUrl },
      traversal: {
        data: {
          debtors: {
            primary: {
              personal: { birthday }
            }
          }
        }
      },
      page: { config }
    }
  } = useRootStore()

  const {
    page: { isFirstDisplayedPage, isLastDisplayedPage, getCurrentIndex },
    navigation: { currentPageInSession },
    traversal: { data, dacWidgetState }
  } = useStores()

  const ageGroup = useAgeGroup()
  const debtorsAge = getAge(birthday)
  const isYoungAgeConditionMet =
    ageGroup === AgeGroup.YOUNG_AGE_GROUP ||
    ageGroup === AgeGroup.TOO_YOUNG_AGE_GROUP

  const isPayoutAccountReady = usePayoutAccountValidation(true)
  const isPageDAC = currentPageInSession === 'dac'

  const { isSaveButtonVisible } = useSaveButton({
    onSuccess: () => {},
    onError: () => {}
  })

  const currentIndex = getCurrentIndex()

  const trackWithOrigin = (event: AmplitudeEvent, payload = {}) => {
    trackInAmplitude(event, { origin: 'taurine', ...payload })
  }

  const onNextWithTracking = useCallback(() => {
    if (ageGroup === AgeGroup.MIDDLE_AGE_GROUP && isPageDAC) {
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: 'skip_dac_button_click_on_dac_page',
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          }
        }
      })
    }
    onNext()
  }, [onNext, data])

  const onBackHandler = () => {
    const isFinalizingOnBackButton =
      ageGroup === AgeGroup.YOUNG_AGE_GROUP && isPageDAC

    if (isLastPage) {
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: 'back_button_click_on_dac_page',
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          }
        }
      })
    }

    if (isFinalizingOnBackButton) {
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: 'final_on_back_button_click',
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          }
        }
      })
      onNext()
      return
    }
    onBack && onBack()
  }

  const isLastPage = isLastDisplayedPage(currentPageInSession)

  useEffect(() => {
    if (
      (isYoungAgeConditionMet ||
        isFFGConfigWithAutoRedirectOnDacSuccess(config.name)) &&
      dacWidgetState === 'success'
    ) {
      trackWithOrigin(AmplitudeEvent.OnDACSuccessAutoRedirect)
      onNextWithTracking()
    }
  }, [dacWidgetState, config.name])

  const isFirstPage = isFirstDisplayedPage(currentPageInSession)

  const backLabel =
    !isInIframe || currentIndex > 0 || backUrl ? 'Zurück' : undefined

  const isBackButtonHidden = !backLabel || isFirstPage

  let submitLabel = isLastPage ? 'Angebote ansehen' : 'Weiter'

  const getIsContinueButtonHidden = () => {
    if (config.name === CONFIG_SECOND_DEBTOR) {
      return false
    }

    if (config.name === CONFIG_DDF) {
      if (
        (isPageDAC && dacWidgetState !== 'success') ||
        (ageGroup !== AgeGroup.OLD_AGE_GROUP && isPageDAC)
      ) {
        return true
      }

      return false
    }

    return (
      isLastPage &&
      isPayoutAccountReady &&
      dacWidgetState !== 'success' &&
      isYoungOrMiddleAgeGroup(debtorsAge)
    )
  }

  const isContinueButtonHidden = getIsContinueButtonHidden()

  const { sizeOfContinueButton, sizeOfBackButton, sizeOfSaveButton } =
    getButtonSizes(
      isBackButtonHidden,
      isSaveButtonVisible,
      isContinueButtonHidden
    )

  const showSkipDacButton =
    (ageGroup === AgeGroup.MIDDLE_AGE_GROUP && isLastPage && isPageDAC) ||
    (config.name === CONFIG_DDF_RCE_EDIT && isPageDAC)

  const showDacLegalText =
    isLastPage &&
    isPayoutAccountReady &&
    isPageDAC &&
    config.name === CONFIG_DDF

  return (
    <FormNavigation
      onBack={onBackHandler}
      onNext={onNext}
      navigate={navigate}
      legalText={LegalHintTexts.defaultLegalHint}
      isContinueWithoutDacVisible={false}
      isBackButtonHidden={isBackButtonHidden}
      isContinueButtonHidden={isContinueButtonHidden}
      sizeOfContinueButton={showSkipDacButton ? 12 : sizeOfContinueButton}
      sizeOfBackButton={sizeOfBackButton}
      sizeOfSaveButton={showSkipDacButton ? 12 : sizeOfSaveButton}
      submitLabel={submitLabel}
      backLabel={backLabel}
      onNextWithTracking={onNextWithTracking}
      trackWithOrigin={trackWithOrigin}
      continueButtonColor={'primary'}
      continueWithoutDacButtonVariant={'text'}
      skipDacButton={
        showSkipDacButton ? (
          <Button
            size="large"
            type="button"
            id="test-submit"
            name="test-submit"
            onClick={onNextWithTracking}
            color="primary"
            fullWidth
            variant={config.name === CONFIG_DDF ? 'contained' : 'outlined'}
          >
            Ich habe keinen Online-Banking-Zugang
          </Button>
        ) : null
      }
      showDacLegalText={showDacLegalText}
      backButtonVariant="text"
      saveButtonVariant={'outlined'}
    />
  )
})

export default memo(FFGFormNavigation)
